/**
 * 자극 사전 렌더링.
 *
 * 트라이얼 중 텍스트 레이아웃·이미지 디코딩은 금지다. 블록 시작 전에 모든 자극을
 * ImageBitmap 으로 구워 메모리에 올린다. 텍스트 자극도 예외 없이 비트맵이다 —
 * fillText 는 글리프 캐시 미스가 나면 프레임을 먹는다.
 *
 * 비트맵은 디바이스 픽셀 해상도로 굽는다: (무대 단위 × stageToDevicePx).
 */
export interface TextStyle {
  fontFamily: string;
  /** 무대 단위 높이 (Renderer 의 geometry.stimulusHeight 와 같은 값) */
  heightStage: number;
  color: string;
  weight?: number;
}

export interface Asset {
  bitmap: ImageBitmap;
  /** 무대 단위 크기 — 그릴 때 이걸로 중심 정렬한다 */
  wStage: number;
  hStage: number;
}

export class AssetCache {
  #assets = new Map<string, Asset>();
  #stageToDevicePx: number;

  constructor(stageToDevicePx: number) { this.#stageToDevicePx = stageToDevicePx; }

  get(id: string): Asset | undefined { return this.#assets.get(id); }
  has(id: string): boolean { return this.#assets.has(id); }
  get size(): number { return this.#assets.size; }

  async prepareText(items: readonly { id: string; text: string }[], style: TextStyle): Promise<void> {
    await document.fonts.ready;
    const px = style.heightStage * this.#stageToDevicePx;
    const font = `${style.weight ?? 500} ${px}px ${style.fontFamily}`;
    // 폰트가 실제로 로드되었는지 한 번 더 강제 (FOUT 방지)
    await document.fonts.load(font, items.map((i) => i.text).join(''));

    const measure = makeCanvas(1, 1).getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
    measure.font = font;

    for (const item of items) {
      if (this.#assets.has(item.id)) continue;
      const m = measure.measureText(item.text);
      const w = Math.ceil(m.width + px * 0.3);
      const h = Math.ceil(px * 1.35);
      const c = makeCanvas(w, h);
      const ctx = c.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
      ctx.font = font;
      ctx.fillStyle = style.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(item.text, w / 2, h / 2);
      const bitmap = await createImageBitmap(c as unknown as ImageBitmapSource);
      this.#assets.set(item.id, { bitmap, wStage: w / this.#stageToDevicePx, hStage: h / this.#stageToDevicePx });
    }
  }

  async prepareImages(items: readonly { id: string; url: string }[], heightStage: number): Promise<void> {
    for (const item of items) {
      if (this.#assets.has(item.id)) continue;
      const res = await fetch(item.url);
      if (!res.ok) throw new Error(`자극 로드 실패: ${item.url}`);
      const blob = await res.blob();
      const raw = await createImageBitmap(blob);
      const scale = (heightStage * this.#stageToDevicePx) / raw.height;
      const bitmap = await createImageBitmap(raw, { resizeWidth: Math.round(raw.width * scale), resizeHeight: Math.round(raw.height * scale), resizeQuality: 'high' });
      raw.close();
      this.#assets.set(item.id, { bitmap, wStage: bitmap.width / this.#stageToDevicePx, hStage: heightStage });
    }
  }

  /** 세션 진입 전 검증: 필요한 에셋이 전부 있는가. 없는 채로 시작하는 것이 최악의 시나리오다. */
  verify(ids: readonly string[]): string[] {
    return ids.filter((id) => !this.#assets.has(id));
  }

  dispose(): void {
    for (const a of this.#assets.values()) a.bitmap.close();
    this.#assets.clear();
  }
}

function makeCanvas(w: number, h: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}
